import { useState, useEffect, useCallback } from "react";
import { avatarBg } from "@/lib/avatar";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, MapPin, MessageCircle, Calendar, Heart, Sparkles, BadgeCheck, Coins, Star, ChevronLeft, ChevronRight, X, EyeOff, Gem, Camera, Crown, HeartHandshake } from "lucide-react";
import Navigation from "@/components/Navigation";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import AiAssistButton from "@/components/AiAssistButton";
import StreakMeter from "@/components/StreakMeter";
import ProfileCompletionCard from "@/components/ProfileCompletionCard";
import { DatePickerField, TimePickerField } from "@/components/DateTimePicker";
import { fireHeartsConfetti, fireDiamondsConfetti } from "@/lib/confetti";
import { useSwipe } from "@/lib/useSwipe";
import { useAppMode } from "@/context/AppModeContext";
import SendOfferModal from "@/components/SendOfferModal";
import SocialInviteDialog from "@/components/SocialInviteDialog";
import SocialInviteTracker from "@/components/SocialInviteTracker";
import AgeVerificationModal from "@/components/AgeVerificationModal";
import { useLocale } from "@/i18n/LocaleContext";

export default function Profile() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { isPayer, appName } = useAppMode();
  const { t, lang } = useLocale();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageDialog, setMessageDialog] = useState(false);
  const [meetupDialog, setMeetupDialog] = useState(false);
  const [sendOfferDialog, setSendOfferDialog] = useState(false);
  const [ageModalOpen, setAgeModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [meetupData, setMeetupData] = useState({
    proposed_date: "",
    proposed_location: "",
    duration_hours: "",
    message: ""
  });
  const [sending, setSending] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [trContent, setTrContent] = useState({});

  const handleSuggest = async () => {
    if (suggesting) return;
    setSuggesting(true);
    try {
      const response = await api.post("/chat/suggest-replies", { receiver_id: userId });
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || t("prof.tSuggestFail"));
    } finally {
      setSuggesting(false);
    }
  };
  const [isFavorite, setIsFavorite] = useState(false);
  const [relStatus, setRelStatus] = useState(null);

  useEffect(() => {
    if (user && userId !== user.user_id) {
      api.get("/relationship/status").then((r) => setRelStatus(r.data)).catch(() => {});
    }
  }, [user?.user_id, userId]);

  const sendGoOfficial = async () => {
    try {
      await api.post("/relationship/request", { partner_id: userId });
      toast.success(t("rel.toastSent").replace("{name}", profile?.name || ""));
      setRelStatus({ status: "outgoing", partner: { user_id: userId } });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("prof.tRequestFail"));
    }
  };
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    setHeroIndex(0);
  }, [userId]);

  const allPhotos = profile?.photos?.length
    ? profile.photos
    : profile?.picture
    ? [profile.picture]
    : [];

  const nextHero = () => allPhotos.length > 1 && setHeroIndex((i) => (i + 1) % allPhotos.length);
  const prevHero = () => allPhotos.length > 1 && setHeroIndex((i) => (i - 1 + allPhotos.length) % allPhotos.length);
  const heroSwipe = useSwipe({ onSwipeLeft: nextHero, onSwipeRight: prevHero });
  const lightboxSwipe = useSwipe({
    onSwipeLeft: () => allPhotos.length > 1 && setLightbox((l) => ({ open: true, index: (l.index + 1) % allPhotos.length })),
    onSwipeRight: () => allPhotos.length > 1 && setLightbox((l) => ({ open: true, index: (l.index - 1 + allPhotos.length) % allPhotos.length })),
  });

  useEffect(() => {
    api.get("/favorites/ids").then((res) => setIsFavorite(res.data.ids.includes(userId))).catch(() => {});
  }, [userId]);

  const toggleFavorite = async () => {
    try {
      const response = await api.post(`/favorites/${userId}`);
      setIsFavorite(response.data.favorited);
      toast.success(response.data.favorited ? t("prof.tFavAdded") : t("prof.tFavRemoved"));
    } catch (error) {
      toast.error("Failed to update favorites");
    }
  };

  const hiddenFromThem = (user?.hidden_from || []).includes(userId);

  const toggleHidden = async () => {
    try {
      const response = await api.post(`/users/hide/${userId}`);
      updateUser(response.data.user);
      toast.success(
        response.data.hidden
          ? t("prof.tHidden", { name: profile?.name || "this member" })
          : t("prof.tVisible", { name: profile?.name || "this member" })
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || t("prof.tVisibilityFail"));
    }
  };

  useEffect(() => {
    api.get(`/date-reviews/user/${userId}`).then((res) => {
      const withComments = (res.data.reviews || []).filter((r) => r.comment);
      if (withComments.length > 0) {
        withComments.sort((a, b) => b.rating - a.rating || new Date(b.created_at) - new Date(a.created_at));
        setHighlight(withComments[0]);
      } else {
        setHighlight(null);
      }
    }).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!profile || lang === "en") {
      setTrContent({});
      return;
    }
    const texts = [];
    const keys = [];
    if (profile.bio) { texts.push(profile.bio); keys.push("bio"); }
    if (highlight?.comment) { texts.push(highlight.comment); keys.push("comment"); }
    (profile.interests || []).slice(0, 8).forEach((it, i) => { texts.push(it); keys.push(`int${i}`); });
    if (!texts.length) {
      setTrContent({});
      return;
    }
    api.post("/translate", { texts, lang }).then((res) => {
      const map = {};
      (res.data.translations || []).forEach((tr, i) => { map[keys[i]] = tr; });
      setTrContent(map);
    }).catch(() => setTrContent({}));
  }, [profile, highlight, lang]);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get(`/users/${userId}`);
      setProfile(response.data);
    } catch (error) {
      console.error("Failed to load profile:", error);
      const detail = error.response?.data?.detail;
      if (detail === "RELATIONSHIP_PRIVATE") {
        toast.error(t("rel.toastPrivate"));
      } else if (detail === "RELATIONSHIP_ACTIVE") {
        toast.error(t("rel.toastActive"));
      } else {
        toast.error(t("prof.tLoadFail"));
      }
      navigate("/discover");
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchProfile();
    
    // Check for action parameter
    const action = searchParams.get("action");
    if (action === "message") {
      setMessageDialog(true);
    } else if (action === "meetup") {
      setMeetupDialog(true);
    } else if (action === "offer") {
      if (!user?.age_verified) {
        setAgeModalOpen(true);
      } else {
        setSendOfferDialog(true);
      }
    }
  }, [fetchProfile, searchParams, setMessageDialog, setMeetupDialog]);

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error(t("prof.tMsgEmpty"));
      return;
    }

    setSending(true);
    try {
      const response = await api.post("/messages/send", {
        receiver_id: userId,
        content: message
      });
      
      toast.success(`Message sent! ${response.data.credits_spent > 0 ? `(${response.data.credits_spent} credits used)` : ""}`);
      setMessageDialog(false);
      setMessage("");
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t("prof.tNoCredits"));
        navigate("/credits");
      } else {
        toast.error(error.response?.data?.detail || t("prof.tMsgFail"));
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendMeetup = async () => {
    if (!meetupData.proposed_date || !meetupData.proposed_location) {
      toast.error(t("prof.tMeetupMissing"));
      return;
    }

    setSending(true);
    try {
      const response = await api.post("/meetups/request", {
        receiver_id: userId,
        ...meetupData,
        duration_hours: meetupData.duration_hours ? parseFloat(meetupData.duration_hours) : null
      });
      
      toast.success(`Meetup request sent! ${response.data.credits_spent > 0 ? `(${response.data.credits_spent} credits used)` : ""}`);
      (user?.gender === "female" ? fireHeartsConfetti : fireDiamondsConfetti)();
      setMeetupDialog(false);
      setMeetupData({ proposed_date: "", proposed_location: "", duration_hours: "", message: "" });
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t("prof.tNoCredits"));
        navigate("/credits");
      } else {
        toast.error(error.response?.data?.detail || t("prof.tMeetupFail"));
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navigation />
      
      <main className="pt-16 pb-24">
        {/* Back button */}
        <div className="px-4 py-4">
          <Button
            variant="ghost"
            className="text-muted-foreground hover:text-white"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("prof.back")}
          </Button>
        </div>

        {/* Profile photo */}
        <div className="relative h-[500px] w-full touch-pan-y overflow-hidden" {...heroSwipe.handlers}>
          {allPhotos.length > 0 ? (
            <>
              <div
                className="absolute inset-0 bg-cover bg-center blur-2xl scale-110 opacity-40"
                style={{ backgroundImage: `url(${allPhotos[heroIndex] || allPhotos[0]})` }}
                aria-hidden="true"
              />
              <img
                src={allPhotos[heroIndex] || allPhotos[0]}
                alt={profile.name}
                className="relative h-full max-w-full mx-auto object-contain cursor-pointer"
                onClick={() => {
                  if (heroSwipe.swiped.current) {
                    heroSwipe.swiped.current = false;
                    return;
                  }
                  setLightbox({ open: true, index: heroIndex });
                }}
                data-testid="main-profile-photo"
              />
            </>
          ) : (
            <div
              className="w-full h-full bg-gradient-to-br from-primary/25 via-background to-secondary/20 flex flex-col items-center justify-center gap-3"
              data-testid="profile-photo-placeholder"
            >
              <div className="w-28 h-28 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-5xl font-bold font-['Outfit']">
                {(profile.name || "?").charAt(0).toUpperCase()}
              </div>
              <p className="text-muted-foreground flex items-center gap-2">
                <Camera className="w-4 h-4" />
                {user?.user_id === userId ? t("prof.addPhotosHint") : t("prof.noPhotos", { name: profile.name })}
              </p>
              {user?.user_id === userId && (
                <Button size="sm" onClick={() => navigate("/settings")} data-testid="add-photos-btn">
                  {t("prof.addPhotos")}
                </Button>
              )}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent pointer-events-none" />
          {allPhotos.length > 1 && (
            <>
              <button
                type="button"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors z-10"
                onClick={prevHero}
                data-testid="hero-prev-photo-btn"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors z-10"
                onClick={nextHero}
                data-testid="hero-next-photo-btn"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <div className="absolute bottom-28 left-1/2 -translate-x-1/2 flex gap-1.5 z-10" data-testid="hero-photo-dots">
                {allPhotos.map((_, i) => (
                  <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === heroIndex ? "bg-white" : "bg-white/30"}`} />
                ))}
              </div>
            </>
          )}
          {user?.user_id !== userId && (
            <>
              <button
                type="button"
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors z-10"
                onClick={toggleFavorite}
                data-testid="profile-favorite-btn"
              >
                <Heart className={`w-6 h-6 transition-colors ${isFavorite ? "text-primary fill-primary" : "text-white"}`} />
              </button>
              <button
                type="button"
                title={hiddenFromThem ? "Show my profile to them again" : "Hide my profile from them"}
                className="absolute top-20 right-4 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center hover:bg-black/60 transition-colors z-10"
                onClick={toggleHidden}
                data-testid="profile-hide-btn"
              >
                <EyeOff className={`w-6 h-6 transition-colors ${hiddenFromThem ? "text-primary" : "text-white"}`} />
              </button>
            </>
          )}
        </div>

        {/* Profile info */}
        <div className="relative px-6 -mt-24">
          <div className="max-w-lg mx-auto">
            {/* Name and verification */}
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-4xl font-bold font-['Outfit'] tracking-tight">
                {profile.name}{profile.age ? `, ${profile.age}` : ""}
              </h1>
              {profile.is_verified && (
                <BadgeCheck className="w-7 h-7 text-primary fill-primary/20" />
              )}
              {profile.is_ai && (
                <span className="px-2.5 py-1 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-300 text-xs font-semibold flex items-center gap-1" data-testid="profile-ai-badge">
                  <Sparkles className="w-3 h-3" /> {t("prof.aiCompanion")}
                </span>
              )}
              {profile.diamond_badge && (
                <Gem className="w-6 h-6 text-cyan-400" data-testid="profile-diamond-badge" />
              )}
              {profile.fire_badge && (
                <span className="text-2xl" title="One of the most-contacted men on DLuxe this month" data-testid="profile-fire-badge">🔥</span>
              )}
            </div>

            {profile.is_ai && user?.user_id !== userId && (
              <div className="mb-4">
                <SocialInviteDialog companion={profile} />
                <SocialInviteTracker companionId={profile.user_id} compact />
              </div>
            )}

            {/* Date rating */}
            {profile.ratings_enabled !== false && profile.review_count > 0 && (
              <div className="flex items-center gap-1.5 mb-2 text-amber-400" data-testid="profile-rating">
                <Star className="w-4 h-4 fill-amber-400" />
                <span className="font-semibold">{Number(profile.avg_rating).toFixed(1)}</span>
                <span className="text-sm text-muted-foreground">
                  · {profile.review_count} {profile.review_count > 1 ? t("prof.reviewMany") : t("prof.reviewOne")}
                </span>
              </div>
            )}

            {/* Location */}
            {profile.location && (
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <MapPin className="w-4 h-4" />
                <span>{profile.location}</span>
              </div>
            )}

            {/* Profile completion + streak - own profile only */}
            {user?.user_id === userId && <ProfileCompletionCard profile={profile} />}
            {user?.user_id === userId && <StreakMeter />}

            {/* Bio */}
            {profile.bio ? (
              <p className="text-lg text-white/80 leading-relaxed mb-6">
                {trContent.bio || profile.bio}
              </p>
            ) : (
              <p className="text-muted-foreground/70 italic mb-6" data-testid="bio-placeholder">
                {user?.user_id === userId
                  ? t("prof.bioEmptyOwn")
                  : t("prof.bioEmptyOther", { name: profile.name })}
              </p>
            )}

            {/* Review highlight */}
            {highlight && (
              <div className="mb-8 p-5 rounded-2xl bg-amber-500/5 border border-amber-500/20" data-testid="review-highlight">
                <div className="flex items-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 ${s <= highlight.rating ? "text-amber-400 fill-amber-400" : "text-white/15"}`}
                    />
                  ))}
                </div>
                <p className="text-white/90 italic leading-relaxed">"{trContent.comment || highlight.comment}"</p>
                <p className="text-xs text-muted-foreground mt-2">{t("prof.afterDate", { name: highlight.reviewer_name })}</p>
              </div>
            )}

            {/* Interests */}
            {profile.interests?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t("prof.interests")}</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm"
                    >
                      {trContent[`int${index}`] || interest}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* More photos */}
            {profile.photos?.length > 1 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t("prof.photos")}</h3>
                <div className="grid grid-cols-3 gap-2">
                  {profile.photos.slice(1).map((photo, index) => (
                    <button
                      type="button"
                      key={index}
                      className="aspect-square rounded-xl overflow-hidden hover:opacity-80 transition-opacity"
                      onClick={() => setLightbox({ open: true, index: index + 1 })}
                      data-testid={`gallery-photo-${index}`}
                    >
                      <img
                        src={photo}
                        alt={`${profile.name} photo ${index + 2}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Videos */}
            {profile.videos?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium text-muted-foreground mb-3">{t("prof.videos")}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {profile.videos.map((video, index) => (
                    <video
                      key={index}
                      src={video}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full aspect-video rounded-xl bg-black object-cover"
                      data-testid={`profile-video-${index}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            {user?.user_id !== userId && (
              <div className="grid grid-cols-2 gap-4 mt-8">
                {isPayer && profile.gender && profile.gender !== user?.gender && (
                  <Button
                    className="h-14 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold col-span-2"
                    onClick={() => {
                      if (!user?.age_verified) {
                        setAgeModalOpen(true);
                      } else {
                        setSendOfferDialog(true);
                      }
                    }}
                    data-testid="profile-premium-offer-btn"
                  >
                    <Crown className="w-5 h-5 mr-2" />
                    {t("discover.sendOffer")}
                    <span className="ml-2 text-xs opacity-90">{profile.is_ai ? t("common.freeTest") : "$100-$1000"}</span>
                  </Button>
                )}
                <Button
                  className="h-14 rounded-xl bg-primary hover:bg-primary/90 glow-primary"
                  onClick={() => setMessageDialog(true)}
                  data-testid="send-message-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {t("profile.message")}
                </Button>
                <Button
                  className="h-14 rounded-xl gradient-gold text-black font-semibold glow-gold"
                  onClick={() => setMeetupDialog(true)}
                  data-testid="request-meetup-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  {t("profile.requestMeetup")}
                </Button>
              </div>
            )}

            {/* Go Official (couple mode) */}
            {!profile.is_ai && user?.user_id !== userId && relStatus && (
              relStatus.status === "none" ? (
                <Button
                  variant="outline"
                  className="w-full mt-4 h-11 rounded-xl border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                  onClick={sendGoOfficial}
                  data-testid="go-official-btn"
                >
                  <HeartHandshake className="w-4 h-4 mr-2" />
                  {t("rel.goOfficial")}
                </Button>
              ) : relStatus.status === "outgoing" && relStatus.partner?.user_id === userId ? (
                <div className="w-full mt-4 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/25 text-center text-sm text-rose-300" data-testid="go-official-pending">
                  {t("rel.requestSent").replace("{name}", profile.name)}
                </div>
              ) : relStatus.status === "active" && relStatus.partner?.user_id === userId ? (
                <div className="w-full mt-4 py-2.5 rounded-xl bg-rose-500/10 border border-rose-500/25 text-center text-sm text-rose-300" data-testid="go-official-active">
                  {relStatus.since ? t("rel.togetherSince").replace("{date}", new Date(relStatus.since).toLocaleDateString(lang)) : t("rel.together")}
                </div>
              ) : null
            )}

            {/* Credit costs info for male users */}
            {isPayer && user?.user_id !== userId && !profile.is_ai && (
              <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Coins className="w-4 h-4" />
                  <span>{t("profile.costsMsg")}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground text-sm mt-1">
                  <Coins className="w-4 h-4" />
                  <span>{t("profile.costsMeetup")}</span>
                </div>
                <Button
                  variant="link"
                  className="text-secondary p-0 h-auto mt-2"
                  onClick={() => navigate("/credits")}
                >
                  Your balance: {user?.credits || 0} credits
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Photo Lightbox */}
      {lightbox.open && allPhotos.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => {
            if (lightboxSwipe.swiped.current) {
              lightboxSwipe.swiped.current = false;
              return;
            }
            setLightbox({ open: false, index: 0 });
          }}
          {...lightboxSwipe.handlers}
          data-testid="photo-lightbox"
        >
          <button
            type="button"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors z-10"
            onClick={() => setLightbox({ open: false, index: 0 })}
            data-testid="lightbox-close-btn"
          >
            <X className="w-6 h-6" />
          </button>

          {allPhotos.length > 1 && (
            <button
              type="button"
              className="absolute left-3 md:left-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((l) => ({ open: true, index: (l.index - 1 + allPhotos.length) % allPhotos.length }));
              }}
              data-testid="lightbox-prev-btn"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}

          <img
            src={allPhotos[lightbox.index]}
            alt={`${profile.name} enlarged`}
            className="max-h-[88vh] max-w-[92vw] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          {allPhotos.length > 1 && (
            <button
              type="button"
              className="absolute right-3 md:right-6 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation();
                setLightbox((l) => ({ open: true, index: (l.index + 1) % allPhotos.length }));
              }}
              data-testid="lightbox-next-btn"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          )}

          {allPhotos.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-sm text-white/80">
              {lightbox.index + 1} / {allPhotos.length}
            </div>
          )}
        </div>
      )}

      {/* Message Dialog */}
      <Dialog open={messageDialog} onOpenChange={setMessageDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{t("profile.msgTitle").replace("{name}", profile.name)}</DialogTitle>
            <DialogDescription>
              {isPayer
                ? t("profile.msgCostPayer")
                : t("profile.msgFree").replaceAll("{app}", appName)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {suggestions.length > 0 && (
              <div data-testid="profile-ai-suggestions">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-primary" /> {user?.gender === "female" ? "AI Cupid" : "AI Wingman"} openers — tap to use
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuggestions([])}
                    className="text-muted-foreground hover:text-white"
                    data-testid="profile-dismiss-suggestions-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      className="text-left text-sm px-4 py-2.5 rounded-xl bg-primary/10 border border-primary/20 hover:bg-primary/20 transition-colors"
                      onClick={() => {
                        setMessage(s);
                        setSuggestions([]);
                      }}
                      data-testid={`profile-ai-suggestion-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Textarea
              placeholder={t("form.writeMessage")}
              className="min-h-[120px] bg-white/5 border-white/10"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="message-input"
            />
            <Button
              type="button"
              variant="outline"
              className="w-full border-primary/30 text-primary hover:bg-primary/10"
              onClick={handleSuggest}
              disabled={suggesting}
              data-testid="profile-ai-suggest-btn"
            >
              {suggesting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary mr-2"></div>
                  {t("prof.thinking")}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t("prof.suggestOpener", { ai: user?.gender === "female" ? "AI Cupid" : "AI Wingman" })}
                </>
              )}
            </Button>
            <Button
              className="w-full bg-primary hover:bg-primary/90"
              onClick={handleSendMessage}
              disabled={sending}
              data-testid="send-message-confirm-btn"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              ) : (
                <>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {t("profile.sendMessage")}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meetup Dialog */}
      <Dialog open={meetupDialog} onOpenChange={setMeetupDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{t("profile.meetupTitle").replace("{name}", profile.name)}</DialogTitle>
            <DialogDescription>
              {profile.is_ai
                ? t("profile.meetupTest")
                : isPayer
                ? t("profile.meetupCost")
                : t("profile.meetupFree").replaceAll("{app}", appName)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t("form.proposedDateTime")}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <DatePickerField
                  id="meetup-date-picker"
                  value={(meetupData.proposed_date || "").split("T")[0]}
                  onChange={(d) => {
                    const t = (meetupData.proposed_date || "").split("T")[1] || "19:00";
                    setMeetupData({ ...meetupData, proposed_date: `${d}T${t}` });
                  }}
                />
                <TimePickerField
                  id="meetup-time-picker"
                  value={(meetupData.proposed_date || "").split("T")[1] || ""}
                  onChange={(t) => {
                    const d = (meetupData.proposed_date || "").split("T")[0] || new Date().toISOString().split("T")[0];
                    setMeetupData({ ...meetupData, proposed_date: `${d}T${t}` });
                  }}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="meetup-duration">{t("form.duration")}</Label>
              <select
                id="meetup-duration"
                className="mt-1 flex h-10 w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                value={meetupData.duration_hours}
                onChange={(e) => setMeetupData({ ...meetupData, duration_hours: e.target.value })}
                data-testid="meetup-duration-select"
              >
                <option value="">{t("form.openEnded")}</option>
                {[1, 1.5, 2, 2.5, 3, 4, 5, 6, 8].map((h) => (
                  <option key={h} value={h}>{h} hour{h === 1 ? "" : "s"}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="meetup-location">{t("form.proposedLocation")}</Label>
              <LocationAutocomplete
                id="meetup-location"
                placeholder={t("form.searchVenue")}
                className="mt-1 bg-white/5 border-white/10"
                value={meetupData.proposed_location}
                onChange={(v) => setMeetupData({ ...meetupData, proposed_location: v })}
              />
            </div>
            <div>
              <Label htmlFor="message">{t("form.messageOptional")}</Label>
              <Textarea
                id="message"
                placeholder={t("form.addPersonalMessage")}
                className="mt-1 bg-white/5 border-white/10"
                value={meetupData.message}
                onChange={(e) => setMeetupData({ ...meetupData, message: e.target.value })}
                data-testid="meetup-message-input"
              />
              <div className="mt-2">
                <AiAssistButton
                  context="meetup_message"
                  receiverId={userId}
                  extra={{
                    location: meetupData.proposed_location,
                    date: (meetupData.proposed_date || "").split("T")[0],
                  }}
                  onPick={(text) => setMeetupData((prev) => ({ ...prev, message: text }))}
                  label="write my invite"
                />
              </div>
            </div>
            <Button
              className="w-full gradient-gold text-black font-semibold"
              onClick={handleSendMeetup}
              disabled={sending}
              data-testid="send-meetup-confirm-btn"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black"></div>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  {t("profile.sendMeetup")}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AgeVerificationModal
        isOpen={ageModalOpen}
        onClose={() => setAgeModalOpen(false)}
        onVerified={() => setSendOfferDialog(true)}
      />

      <SendOfferModal
        isOpen={sendOfferDialog}
        onClose={() => setSendOfferDialog(false)}
        woman={profile}
        onSuccess={() => {
          toast.success(t("prof.offerSent"));
        }}
      />
    </div>
  );
}
